import { Observable } from 'rxjs'
import Member from 'iam/models/Member'
import { tap } from 'rxjs/operators'
import { saturate, darken, desaturate, lighten, parseToRgb } from 'polished'
import Workspace from 'iam/models/Workspace'
import memoize from 'lodash/memoize'
import { WorkspaceDTO } from 'iam/dtos/workspace'

function toRgbStr(color: string) {
  const asRgb = parseToRgb(color)
  return `${asRgb.red}, ${asRgb.green}, ${asRgb.blue}`
}

function setBodyCssVar(variable: string, value: string) {
  document.body.style.setProperty(variable, value)
}

// Workspace Branding Config
interface BrandingConfig {
  themePrimary: string
  themeDarken: string
  themeLighten: string
  corporateMode: boolean
  logoPrimary: string
  logoPrimaryWhite: string
  logoIcon: string
  logoIconWhite: string
  landingPageImage: string
  workspaceName: string
}

export const getBrandingSettings = memoize(
  (workspace: Workspace | WorkspaceDTO) => _getBrandingSettings(workspace),
)

function _getBrandingSettings(
  workspace: Workspace | WorkspaceDTO,
): BrandingConfig {
  const branding = workspace.branding
  const primary = branding && branding.colors && branding.colors.primary

  const themePrimary = primary
  const primaryDarken = saturate(0.3, darken(0.1, primary))
  const primaryLighten = desaturate(0.1, lighten(0.3, primary))

  const corporateModeOn = workspace.branding.corporate.enabled

  const workspaceName = workspace.name || 'Worklayer'

  const logoPrimary =
    workspace.branding.logos.primary ||
    require('assets/logos/logo_worklayer-blue.svg')

  const logoPrimaryWhite =
    workspace.branding.logos.primary_white ||
    'https://storage.googleapis.com/worklayer-assets/default-workspace-branding/logo-white.svg'

  const logoIcon =
    workspace.branding.logos.icon ||
    'https://storage.googleapis.com/taxfyle-assets-public/taxfyle-workspace-assets/taxfyle-icon-blue.png'

  const logoIconWhite =
    workspace.branding.logos.icon_white ||
    'https://storage.googleapis.com/worklayer-assets/default-workspace-branding/icon-white.svg'

  const landingPageImage =
    workspace.branding.landing_page.image ||
    require('assets/images/img_login.svg')

  return {
    corporateMode: corporateModeOn,
    themePrimary,
    themeDarken: primaryDarken,
    themeLighten: primaryLighten,
    logoPrimary,
    logoPrimaryWhite,
    logoIcon,
    logoIconWhite,
    landingPageImage,
    workspaceName,
  }
}

function setBrandingVariables(workspace: Workspace | WorkspaceDTO) {
  const { themePrimary, themeDarken, themeLighten } =
    getBrandingSettings(workspace)

  setBodyCssVar('--theme-base', themePrimary)
  setBodyCssVar('--theme-darken', themeDarken)
  setBodyCssVar('--theme-lighten', themeLighten)
  setBodyCssVar('--theme-base-rgb', toRgbStr(themePrimary))
  setBodyCssVar('--theme-darken-rgb', toRgbStr(themeDarken))
  setBodyCssVar('--theme-lighten-rgb', toRgbStr(themeLighten))
}

/**
 * Set theme CSS variable values from workspace
 * branding config every time the workspace
 * changes. i.e. when currentMember$ emits a new value
 * and in the initial render with the values from
 * the server
 */
export function BoostrapTheming(currentMember$: Observable<Member>) {
  if (
    window &&
    window.__workspaceDomain &&
    window.__workspaceDomain.workspace
  ) {
    const serverWorkspace = window.__workspaceDomain.workspace
    setBrandingVariables(serverWorkspace)
  }

  return currentMember$.pipe(
    tap((member) => {
      const workspace = member.workspace
      setBrandingVariables(workspace)
    }),
  )
}
